import { useRef } from "react";
import { ChevronDownIcon } from "lucide-react";

import { Column, RichTextDisplay, Row, Text } from "@/components";
import { StructuredVoiceAISummary } from "./voice-ai-call/StructuredVoiceAISummary";
import { StatusBadge } from "./voice-ai-call/StatusBadge";
import { VoiceAICallWithTranscript } from "./voice-ai-call/VoiceAICallWithTranscript";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

import type { EnrichmentCandidacy } from "./types";

import { useSheetScope } from "@/contexts/SheetScopeContext";
import { dates } from "@/lib/dates";
import { cn } from "@/lib/utils";
import { VoiceAIOutreach } from "@/types";

export const EnrichmentStevieCallItem = ({
  candidacy,
  isSelected,
  onToggle,
  voiceAIOutreach,
}: {
  candidacy: EnrichmentCandidacy;
  isSelected: boolean;
  onToggle: () => void;
  voiceAIOutreach: VoiceAIOutreach;
}) => {
  /* Contexts */
  const { job } = useSheetScope();

  /* React Hooks */
  // useRef
  const audioRef = useRef<HTMLAudioElement>(null);

  /* Helpers */
  // Nova types names as non-optional but omits unset fields at runtime.
  const candidateFirstName =
    (candidacy.profile.profile as { firstName?: string }).firstName ?? "";
  const candidateLastName =
    (candidacy.profile.profile as { lastName?: string }).lastName ?? "";
  const formattedDate =
    dates.formatIsoDateTimeForUser(voiceAIOutreach.scheduledDateTime) ?? "";

  /* Render */
  const renderSummary = () => {
    if (voiceAIOutreach.aiSummaryJson) {
      return (
        <StructuredVoiceAISummary
          audioRef={audioRef}
          summaryJson={voiceAIOutreach.aiSummaryJson}
        />
      );
    }
    if (voiceAIOutreach.aiSummary) {
      return <RichTextDisplay richText={voiceAIOutreach.aiSummary} />;
    }
    return null;
  };

  const render = () => {
    return (
      <Collapsible
        className={cn(
          "group/stevie-call overflow-hidden rounded-lg border bg-white",
          isSelected ? "border-primary-300" : "border-slate-200",
        )}
      >
        <Row className="items-center gap-3 p-3">
          <Checkbox
            aria-label={`Include call from ${formattedDate}`}
            checked={isSelected}
            onCheckedChange={onToggle}
          />
          <CollapsibleTrigger className="flex flex-1 cursor-pointer items-center gap-2 text-left">
            <Text className="font-medium text-slate-700">{formattedDate}</Text>
            <StatusBadge status={voiceAIOutreach.status} />
            <ChevronDownIcon className="ml-auto h-4 w-4 shrink-0 text-slate-400 transition-transform group-data-[state=closed]/stevie-call:-rotate-90" />
          </CollapsibleTrigger>
        </Row>
        <CollapsibleContent>
          <Column className="gap-4 border-t border-slate-200 p-4">
            {voiceAIOutreach.audioUrl && voiceAIOutreach.transcript && (
              <VoiceAICallWithTranscript
                audioRef={audioRef}
                candidacyId={candidacy.candidacyId}
                candidateFirstName={candidateFirstName}
                candidateLastName={candidateLastName}
                jobTitle={job.title}
                transcript={voiceAIOutreach.transcript}
                voiceAIOutreachId={voiceAIOutreach.id}
              />
            )}
            {renderSummary()}
          </Column>
        </CollapsibleContent>
      </Collapsible>
    );
  };

  return render();
};
